import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Factory, Activity, Zap, Clock, TrendingUp, Loader2, RefreshCw, ChevronRight, ChevronDown, GitBranch, Home } from 'lucide-react'
import { Card } from '../../components/ui/Card'
import { Badge } from '../../components/ui/Badge'
import { api } from '../../lib/api'
import { useLanguage } from '../../context/LanguageContext'
import { usePermissions } from '../../hooks/usePermissions'
import { useFactory } from '../../context/FactoryContext'
import { workOrdersService } from './services/workOrdersService'
import { assetService } from '../../services/activos/assetService';
import { KPIBar } from './components/kpi/KPIBar';
import { KpiLine } from './components/kpi/KpiLine';
import { KpiLineDetails } from './components/kpi/KpiLineDetails';
import { KpiAssetTree } from './components/kpi/KpiAssetTree';

// Orden fijo de fábricas (orden descendente de prioridad)
const FACTORY_ORDER = [
    'FPC', 'FPL', 'FSP', 'CXM', 'CXF', 'CXE', 'CXD', 'CXC', 'CXB',
    'EXT', 'MNT', 'RTP', 'ITC'
]

const graphdimensions = {
    width: 300,
    height: 300,
    radius: 125,
    perifery: 2 * Math.PI * 125,
}

const getCoordinatesForPercent = (percent) => {
    const angle = (percent / 100) * 360 * (Math.PI / 180);
    const cx = graphdimensions.width / 2;
    const cy = graphdimensions.height / 2;
    const r = graphdimensions.radius;
    return {
        x: cx + r * Math.cos(angle),
        y: cy + r * Math.sin(angle)
    };
};


function KPI() {
    const navigate = useNavigate()
    const { t } = useLanguage()
    const { permissions, checkFactoryAccess } = usePermissions()
    const { selectedFactory, setSelectedFactory } = useFactory()

    const [lines, setLines] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [lastUpdate, setLastUpdate] = useState(null)
    const [selectedLine, setSelectedLine] = useState(null)
    const [selectedAssetCode, setSelectedAssetCode] = useState(null)
    const [expandedLineTrees, setExpandedLineTrees] = useState(new Set())
    const [lastThroughputUpdate, setLastThroughputUpdate] = useState(null)

    //Botón para expandir/colapsar líneas de producción
    const toggleLineTree = (lineId) => {
        setExpandedLineTrees(prev => {
            const next = new Set(prev);
            if (next.has(lineId)) {
                next.delete(lineId);
            } else {
                next.add(lineId);
            }
            return next;
        });
    };

    const handleSelectAsset = (line, assetCode, e) => {
        e?.stopPropagation?.();
        if (selectedLine?.id !== line.id) {
            setSelectedLine(line);
        }
        setSelectedAssetCode(assetCode);
    };

    //Filtro fechas
    const [startDate, setStartDate] = useState(() => {
        const d = new Date()
        d.setMonth(d.getMonth() - 1)
        return d.toISOString().split('T')[0]
    })
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0])

    const [chartJsLoaded, setChartJsLoaded] = useState(false);


    // Fetch all Work Orders from factory
    const {
        data: workOrdersResponse,
        isLoading: isLoadingWorkOrders,
        isFetching: isFetchingWorkOrders,
        isError: isErrorWorkOrders,
        error: workOrdersError,
        refetch: refetchWorkOrders
    } = useQuery({
        queryKey: ['workOrders', selectedFactory, startDate, endDate],
        queryFn: () => workOrdersService.list({ factory_name: selectedFactory, from_date: startDate, to_date: endDate }, 1, 1000),
        retry: 1,
    });

    // Fetch associated assets from Work Orders 
    const {
        data: assetResponse,
        isLoading: isLoadingAsset,
        isError: isErrorAsset,
        error: assetError,
        refetch: refetchAsset
    } = useQuery({
        queryKey: ['asset', selectedFactory, workOrdersResponse],
        queryFn: async () => {
            const workOrders = workOrdersResponse?.data || [];

            // Obtener IDs de activos únicos
            const uniqueAssetIds = [...new Set(workOrders.map(wo => wo.asset_id))];

            // Fetch de jerarquía solo para activos únicos
            const hierarchyMap = {};
            await Promise.all(
                uniqueAssetIds.map(async (assetId) => {
                    try {
                        const res = await assetService.getAssetHierarchy(assetId);
                        hierarchyMap[assetId] = res?.HierarchicalLevel || [];
                    } catch (err) {
                        console.error(`Error fetching hierarchy for asset ${assetId}:`, err);
                        hierarchyMap[assetId] = [];
                    }
                })
            );

            // Mapear los resultados de vuelta a cada orden de trabajo
            return workOrders.map((workOrder) => ({
                workOrder: workOrder,
                hierarchical: hierarchyMap[workOrder.asset_id] || []
            }));
        },
        retry: 1,
        enabled: !!workOrdersResponse?.data
    });

    // Originally we would filter based on the scheduled_date if the WO was pending, and based on start_date if the WO had started.
    // However, to save network traffic we filter directly from the workOrdersResponse query, and that filter only filters by scheduled_date.
    const allWorkOrders = useMemo(() => {
        const rawWOs = workOrdersResponse?.data || [];
        return rawWOs;
        // return rawWOs.filter(wo => {
        //     const status = wo.status?.toLowerCase();
        //     const dateStr = (status === 'scheduled' || status === 'pending')
        //         ? wo.scheduled_date
        //         : wo.start_date;
        //     if (!dateStr) return !startDate && !endDate;

        //     const woDate = new Date(dateStr);
        //     if (startDate) {
        //         const start = new Date(startDate);
        //         start.setHours(0, 0, 0, 0);
        //         if (woDate < start) return false;
        //     }
        //     if (endDate) {
        //         const end = new Date(endDate);
        //         end.setHours(23, 59, 59, 999);
        //         if (woDate > end) return false;
        //     }
        //     return true;
        // });
    }, [workOrdersResponse, startDate, endDate]);

    const workOrdersByPriorities = useMemo(() => {

        const total = allWorkOrders?.length;
        let remainingWorkOrders = allWorkOrders?.filter(wo => wo.priority !== 'low') || [];
        const lowWOs = total - remainingWorkOrders.length;
        remainingWorkOrders = remainingWorkOrders.filter(wo => wo.priority !== 'medium') || [];
        const mediumWOs = total - remainingWorkOrders.length - lowWOs;
        remainingWorkOrders = remainingWorkOrders.filter(wo => wo.priority !== 'high') || [];
        const highWOs = total - remainingWorkOrders.length - lowWOs - mediumWOs;
        const criticalWOs = remainingWorkOrders.length;

        return {
            low: lowWOs,
            lowPct: total > 0 ? (lowWOs / total * 100) : 0,
            medium: mediumWOs,
            mediumPct: total > 0 ? (mediumWOs / total * 100) : 0,
            high: highWOs,
            highPct: total > 0 ? (highWOs / total * 100) : 0,
            critical: criticalWOs,
            criticalPct: total > 0 ? (criticalWOs / total * 100) : 0
        }
    }, [allWorkOrders]);

    const workOrdersByStatus = useMemo(() => {
        const total = allWorkOrders?.length || 0;
        if (total === 0) {
            return {
                total: 0,
                completed: 0,
                completedPct: 0,
                inProgress: 0,
                inProgressPct: 0,
                delayed: 0,
                delayedPct: 0,
                scheduled: 0,
                scheduledPct: 0,
                passed: 0,
                passedPct: 0,
                failed: 0,
                failedPct: 0,
                successRate: 0,
            };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let completedWOs = 0;
        let inProgressWOs = 0;
        let delayedWOs = 0;
        let scheduledWOs = 0;
        let passedWOs = 0;
        let failedWOs = 0;

        allWorkOrders.forEach(wo => {
            const status = wo.status?.toLowerCase();
            if (status === 'completed') {
                completedWOs++;
                if (wo.quality_check) {
                    passedWOs++;
                } else {
                    failedWOs++;
                }
            } else if (status === 'in_progress') {
                inProgressWOs++;
            } else {
                // Scheduled or pending
                if (wo.scheduled_date) {
                    const schedDate = new Date(wo.scheduled_date);
                    schedDate.setHours(0, 0, 0, 0);
                    if (schedDate < today) {
                        delayedWOs++;
                    } else {
                        scheduledWOs++;
                    }
                } else {
                    delayedWOs++;
                }
            }
        });

        const successRate = completedWOs > 0 ? (passedWOs / completedWOs) * 100 : 0;
        const failedRate = completedWOs > 0 ? (failedWOs / completedWOs) * 100 : 0;

        return {
            total,
            completed: completedWOs,
            completedPct: total > 0 ? (completedWOs / total) * 100 : 0,
            inProgress: inProgressWOs,
            inProgressPct: total > 0 ? (inProgressWOs / total) * 100 : 0,
            delayed: delayedWOs,
            delayedPct: total > 0 ? (delayedWOs / total) * 100 : 0,
            scheduled: scheduledWOs,
            scheduledPct: total > 0 ? (scheduledWOs / total) * 100 : 0,
            passed: passedWOs,
            passedPct: successRate,
            failed: failedWOs,
            failedPct: failedRate,
            successRate,
        };
    }, [allWorkOrders]);



    // Categorize work orders by type
    const categorizeLineWorkOrders = (fwo) => {
        if (fwo && fwo?.length > 0) {
            const preventiveWorkOrders = fwo.filter(workOrder => workOrder.work_order_type === 'preventive');
            const correctiveWorkOrders = fwo.filter(workOrder => workOrder.work_order_type === 'corrective');
            const predictiveWorkOrders = fwo.filter(workOrder => workOrder.work_order_type === 'predictive');
            return {
                preventiveWorkOrders,
                correctiveWorkOrders,
                predictiveWorkOrders
            }
        }
        else {
            return {};
        }
    }

    const calculateAvailability = (line) => {
        // MTBF
        // We take only completed corrective work orders
        const correctiveWOs = line?.lineWorkOrders?.correctiveWorkOrders?.filter(wo => wo.status === 'completed').sort((a, b) => new Date(a.start_date) - new Date(b.start_date)) || [];

        let MTBF_unit = "horas";
        let MTTR_unit = "horas";
        // If there are less than 2 completed work orders, we can't calculate MTBF
        if (correctiveWOs.length < 2) return null;

        let MTBF_denom = correctiveWOs.length - 1;
        let MTBF_sum = 0;

        correctiveWOs.forEach((wo, i, arr) => {
            const nextWo = arr[i + 1];
            if (nextWo && wo.end_date) {
                const timeBetweenFailures = new Date(nextWo.start_date).getTime() - new Date(wo.end_date).getTime();
                if (timeBetweenFailures < 0) {
                    MTBF_denom--;
                } else {
                    MTBF_sum += timeBetweenFailures;
                }
            }
        });

        let MTBF = 0;
        if (!(MTBF_denom <= 0)) {
            // Hour format
            MTBF_sum = MTBF_sum / 1000 / 3600;
            MTBF = MTBF_sum / MTBF_denom;
        }

        // MTTR
        let MTTR_sum = 0;
        const MTTR_denom = correctiveWOs.length;

        correctiveWOs.forEach(wo => {
            MTTR_sum += new Date(wo.end_date).getTime() - new Date(wo.start_date).getTime();
        });

        let MTTR = (MTTR_sum / MTTR_denom) / 1000 / 3600;
        const availability = (MTBF / (MTBF + MTTR)) * 100;

        if (MTBF > 96) {
            MTBF_unit = "días";
            MTBF = MTBF / 24;
        }
        if (MTTR > 96) {
            MTTR_unit = "días";
            MTTR = MTTR / 24;
        }

        return {
            MTBF: [MTBF.toFixed(2), MTBF_unit],
            MTTR: [MTTR.toFixed(2), MTTR_unit],
            availability: [availability.toFixed(2)]
        };
    };

    const calculateSuccessRate = (line) => {
        const finishedWOs = [line?.lineWorkOrders?.correctiveWorkOrders, line?.lineWorkOrders?.predictiveWorkOrders, line?.lineWorkOrders?.preventiveWorkOrders].flat().filter(wo => wo?.status === 'completed')
        const total = finishedWOs?.length
        const successful = finishedWOs.reduce((acc, wo) => {
            if (wo?.quality_check === true) {
                acc = acc + 1
            }
            return acc
        }, 0)
        return (successful / total) * 100
    }

    const calculateMonthlyRate = (line) => {
        const workOrders = [
            ...(line?.lineWorkOrders?.correctiveWorkOrders || []),
            ...(line?.lineWorkOrders?.predictiveWorkOrders || []),
            ...(line?.lineWorkOrders?.preventiveWorkOrders || [])
        ];

        if (workOrders.length === 0) return 0;

        const dates = workOrders
            .map(wo => wo.start_date || wo.scheduled_date || wo.created_at)
            .filter(Boolean)
            .map(d => new Date(d));

        if (dates.length === 0) return 1;

        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));

        const diffTime = maxDate - minDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        const numMonths = Math.max(1, diffDays / 30.44);

        return workOrders.length / numMonths;
    }

    const calculateWeeklyRate = (line) => {
        const workOrders = [
            ...(line?.lineWorkOrders?.correctiveWorkOrders || []),
            ...(line?.lineWorkOrders?.predictiveWorkOrders || []),
            ...(line?.lineWorkOrders?.preventiveWorkOrders || [])
        ];

        if (workOrders.length === 0) return 0;

        const dates = workOrders
            .map(wo => wo.start_date || wo.scheduled_date || wo.created_at)
            .filter(Boolean)
            .map(d => new Date(d));

        if (dates.length === 0) return 1;

        const minDate = new Date(Math.min(...dates));
        const maxDate = new Date(Math.max(...dates));

        const diffTime = maxDate - minDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        const numWeeks = Math.max(1, diffDays / 7);

        return workOrders.length / numWeeks;
    }

    const filteredWorkOrders = useMemo(() => {

        if (!assetResponse || !lines) return [];

        const lineCodeSet = new Set(lines.map(line => line.line));

        const workOrdersByLine = new Map();

        assetResponse.forEach(asset => {
            const workOrder = asset.workOrder;
            const status = workOrder.status?.toLowerCase();
            const dateStr = (status === 'scheduled' || status === 'pending')
                ? workOrder.scheduled_date
                : workOrder.start_date;

            let match = true;
            if (!dateStr) {
                match = !startDate && !endDate;
            } else {
                const woDate = new Date(dateStr);
                if (startDate) {
                    const start = new Date(startDate);
                    start.setHours(0, 0, 0, 0);
                    if (woDate < start) match = false;
                }
                if (endDate) {
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    if (woDate > end) match = false;
                }
            }

            if (match) {
                const matchingLineCode = asset.hierarchical?.find(code => lineCodeSet.has(code));
                if (matchingLineCode) {
                    if (!workOrdersByLine.has(matchingLineCode)) {
                        workOrdersByLine.set(matchingLineCode, []);
                    }
                    workOrdersByLine.get(matchingLineCode).push({
                        ...workOrder,
                        hierarchical: asset.hierarchical || []
                    });
                }
            }
        });

        return lines.map(line => {
            const lineWO = categorizeLineWorkOrders(workOrdersByLine.get(line.line)) || { preventiveWorkOrders: [], correctiveWorkOrders: [], predictiveWorkOrders: [] };

            const correctiveCount = lineWO.correctiveWorkOrders?.length || 0;
            const predictiveCount = lineWO.predictiveWorkOrders?.length || 0;
            const preventiveCount = lineWO.preventiveWorkOrders?.length || 0;
            const total = correctiveCount + predictiveCount + preventiveCount;

            const baseLine = {
                ...line,
                lineWorkOrders: lineWO,
                workOrderStats: {
                    total,
                    correctivePct: total > 0 ? (correctiveCount / total) * 100 : 0,
                    predictivePct: total > 0 ? (predictiveCount / total) * 100 : 0,
                    preventivePct: total > 0 ? (preventiveCount / total) * 100 : 0,
                }
            };



            return {
                ...baseLine,
                availabilityStats: calculateAvailability(baseLine),
                successRate: calculateSuccessRate(baseLine),
                monthlyRate: calculateMonthlyRate(baseLine),
                weeklyRate: calculateWeeklyRate(baseLine),
            };
        });


    }, [assetResponse, lines, selectedFactory, startDate, endDate]);



    console.log(startDate, endDate, 'fechas')
    console.log(allWorkOrders, 'U ARE')

    // const calculatevailability = useMemo(() =>{
    //     filteredW
    // });


    useEffect(() => {
        console.log(filteredWorkOrders, 'man')
        console.log(assetResponse, 'womn')
        console.log(workOrdersByPriorities, 'pri')
    }, [filteredWorkOrders])

    useEffect(() => {
        console.log(selectedLine, 'linea')
    }, [selectedLine])

    // Dynamic Chart.js Loader
    useEffect(() => {
        if (window.Chart) {
            setChartJsLoaded(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/chart.js';
        script.async = true;
        script.onload = () => setChartJsLoaded(true);
        document.body.appendChild(script);
    }, []);



    // Recargar líneas cuando cambie selectedFactory (desde el contexto)
    useEffect(() => {
        // Usar un pequeño delay para asegurar que el estado se haya actualizado
        const timer = setTimeout(() => {
            loadLines()
        }, 100)

        return () => clearTimeout(timer)
    }, [selectedFactory]) // Recargar cuando cambie la fábrica

    // useEffect(() => {
    //     // Actualizar estado de líneas cada 30 segundos
    //     const statusInterval = setInterval(() => {
    //         loadLinesStatusOnly()
    //     }, 30000)

    //     // Actualizar throughput cada 20 minutos (1200000 ms)
    //     const throughputInterval = setInterval(() => {
    //         loadLinesThroughput()
    //     }, 20 * 60 * 1000)

    //     return () => {
    //         clearInterval(statusInterval)
    //         clearInterval(throughputInterval)
    //     }
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []) // Solo ejecutar una vez al montar

    const loadLines = async () => {
        try {
            setLoading(true)
            setError(null)

            // Usar /api/asset/list y filtrar las líneas de producción
            const response = await api.get('/api/asset/list')

            // La respuesta podría ser un array directo o un objeto con datos
            let assetsArray = []
            if (Array.isArray(response)) {
                assetsArray = response
            } else if (response && Array.isArray(response.data)) {
                assetsArray = response.data
            } else if (response && Array.isArray(response.assets)) {
                assetsArray = response.assets
            } else {
                setLines([])
                setError('Formato de respuesta inesperado')
                return
            }

            // Normalizar campos como en JerarquiaActivos
            const normalizeAsset = (asset) => {
                return {
                    id: asset.ID !== undefined ? asset.ID : asset.id,
                    code: asset.Code !== undefined ? asset.Code : asset.code,
                    location: asset.Location !== undefined ? asset.Location : asset.location,
                    factory: asset.Factory !== undefined ? asset.Factory : asset.factory,
                    hierarchical_level: asset.HierarchicalLevel !== undefined ? asset.HierarchicalLevel : asset.hierarchical_level,
                    parentId: asset.ParentID !== undefined ? asset.ParentID : asset.ParentId !== undefined ? asset.ParentId : asset.parentId,
                    sap_code: asset.sap_code || asset.SapCode || asset.sapCode,
                    mr_products: asset.mr_products || asset.MrProducts || asset.mrProducts,
                    ...asset // Mantener todos los campos originales
                }
            }

            const normalizedAssets = assetsArray.map(normalizeAsset)
            // Crear mapa de assets por ID para buscar padres
            const assetMap = new Map()
            normalizedAssets.forEach(asset => {
                if (asset.id != null) {
                    assetMap.set(asset.id, asset)
                }
            })

            // Identificar fábricas (nodos raíz sin parentId)
            const factories = new Set()
            normalizedAssets.forEach(asset => {
                if (asset.id != null && (asset.parentId == null || asset.parentId === null || asset.parentId === '')) {
                    factories.add(asset.id)
                }
            })

            // Filtrar líneas de producción
            // Las líneas pueden ser:
            // 1. Assets con hierarchical_level con 2 elementos (Fábrica, Línea)
            // 2. Assets con código que empiece con "Line_" o contenga "LINE"
            // 3. Assets que son hijos directos de fábricas (parentId apunta a una fábrica)
            const lines = normalizedAssets
                .filter(asset => {
                    // Opción 1: Filtrar por hierarchical_level (debe tener exactamente 2 niveles: Fábrica y Línea)
                    if (asset.hierarchical_level && Array.isArray(asset.hierarchical_level)) {
                        return asset.hierarchical_level.length === 2
                    }

                    // Opción 2: Filtrar por código que empiece con "Line_" o contenga "LINE"
                    if (asset.code) {
                        const code = asset.code.toUpperCase()
                        return code.startsWith('LINE_') || code.includes('LINE')
                    }

                    // Opción 3: Si tiene parentId y el padre es una fábrica, es una línea
                    if (asset.parentId != null && factories.has(asset.parentId)) {
                        return true
                    }

                    return false
                })
                .map(asset => {
                    // Extraer fábrica del hierarchical_level, del campo factory, o del padre
                    let factory = ''
                    if (asset.hierarchical_level && Array.isArray(asset.hierarchical_level) && asset.hierarchical_level.length > 0) {
                        factory = asset.hierarchical_level[0]
                    } else if (asset.factory) {
                        factory = asset.factory
                    } else if (asset.parentId != null && factories.has(asset.parentId)) {
                        // Si es hijo de una fábrica, obtener el código de la fábrica
                        const parentAsset = assetMap.get(asset.parentId)
                        if (parentAsset) {
                            factory = parentAsset.code || ''
                        }
                    }

                    // Extraer información de estado si está disponible
                    return {
                        id: asset.id,
                        line: asset.code || '',
                        factory: factory,
                        //location: asset.location || '',
                        //status: asset.status || 'unknown',
                        //oee: asset.oee || 0,
                        //speed: asset.speed || 0,
                        //throughput: asset.throughput || 0,
                        //production: asset.production || 0,
                        //influx_available: asset.influx_available || false,
                        //last_update: asset.last_update || asset.lastUpdate || null
                    }
                })

            const factoryToFilter = selectedFactory

            // Filtrar por fábrica si está seleccionada (excluir 'CX' que es vista global)
            let filteredLines = lines
            if (factoryToFilter && factoryToFilter !== 'CX') {
                filteredLines = lines.filter(line => {
                    const lineFactory = line.factory ? line.factory.toUpperCase().trim() : ''
                    const selected = factoryToFilter.toUpperCase().trim()
                    return lineFactory === selected
                })
            }

            // Obtener estados y throughput desde InfluxDB si hay líneas y una fábrica seleccionada
            if (filteredLines.length > 0 && factoryToFilter && factoryToFilter !== 'CX') {
                await loadLinesStatus(filteredLines, factoryToFilter, true) // true = incluir throughput en carga inicial
            } else {
                // Si no hay fábrica seleccionada o es 'CX', mostrar todas las líneas (vista global)
                // Si hay fábrica seleccionada pero no hay líneas, mostrar array vacío
                setLines(filteredLines)
            }

            setLastUpdate(new Date())
            setError(null)
        } catch (err) {
            console.error('Error loading lines:', err)
            console.error('Error details:', {
                message: err.message,
                stack: err.stack,
                name: err.name
            })
            setError(`Error de conexión: ${err.message || 'No se pudo conectar al servidor'}`)
            setLines([])
        } finally {
            setLoading(false)
        }
    }

    // // Función para cargar el estado de las líneas desde InfluxDB
    const loadLinesStatus = async (lines, factory, includeThroughput = false) => {
        try {
            // Extraer códigos de líneas
            const lineCodes = lines.map(line => line.line).filter(code => code)

            if (lineCodes.length === 0) {
                setLines(lines)
                return
            }

            // Consultar estado desde InfluxDB
            const statusResponse = await api.get(`/api/asset/lines/status?factory=${factory}&lines=${lineCodes.join(',')}`)

            // Actualizar líneas con el estado y throughput desde InfluxDB
            const updatedLines = lines.map(line => {
                const statusInfo = statusResponse[line.line]
                if (statusInfo) {
                    const newThroughput = includeThroughput ? (statusInfo.throughput || 0) : (line.throughput || 0)
                    return {
                        ...line,
                        //status: statusInfo.status === 'operativa' ? 'running' :
                        //statusInfo.status === 'apagada' ? 'stopped' : 'unknown',
                        //influx_available: statusInfo.status !== 'unknown',
                        // Solo actualizar throughput si includeThroughput es true
                        //throughput: newThroughput,
                        //last_update: statusInfo.last_seen || line.last_update
                    }
                }
                return {
                    ...line,
                    //status: 'unknown',
                    //influx_available: false,
                    //throughput: includeThroughput ? 0 : (line.throughput || 0)
                }
            })

            setLines(updatedLines)
            if (includeThroughput) {
                setLastThroughputUpdate(new Date())
            }
        } catch (err) {
            console.error('Error loading lines status from InfluxDB:', err)
            // Si falla, usar las líneas sin estado actualizado
            setLines(lines)
        }
    }

    // // Función para actualizar solo el estado (sin throughput)
    // const loadLinesStatusOnly = async () => {
    //     if (lines.length === 0 || !selectedFactory) return

    //     // Asegurar que solo trabajamos con líneas de la fábrica seleccionada
    //     const filteredLines = lines.filter(line => {
    //         const lineFactory = line.factory ? line.factory.toUpperCase().trim() : ''
    //         const selected = selectedFactory.toUpperCase().trim()
    //         return lineFactory === selected
    //     })

    //     if (filteredLines.length === 0) return

    //     try {
    //         const lineCodes = filteredLines.map(line => line.line).filter(code => code)
    //         if (lineCodes.length === 0) return

    //         const statusResponse = await api.get(`/api/asset/lines/status?factory=${selectedFactory}&lines=${lineCodes.join(',')}`)

    //         const updatedLines = filteredLines.map(line => {
    //             const statusInfo = statusResponse[line.line]
    //             if (statusInfo) {
    //                 return {
    //                     ...line,
    //                     //status: statusInfo.status === 'operativa' ? 'running' :
    //                     //statusInfo.status === 'apagada' ? 'stopped' : 'unknown',
    //                     //influx_available: statusInfo.status !== 'unknown',
    //                     // Mantener el throughput existente
    //                     //throughput: line.throughput || 0,
    //                     //last_update: statusInfo.last_seen || line.last_update
    //                 }
    //             }
    //             return line
    //         })

    //         setLines(updatedLines)
    //         setLastUpdate(new Date())
    //     } catch (err) {
    //         console.error('Error loading lines status:', err)
    //     }
    // }

    // // Función para actualizar solo el throughput
    // const loadLinesThroughput = async () => {
    //     if (lines.length === 0 || !selectedFactory) return

    //     // Asegurar que solo trabajamos con líneas de la fábrica seleccionada
    //     const filteredLines = lines.filter(line => {
    //         const lineFactory = line.factory ? line.factory.toUpperCase().trim() : ''
    //         const selected = selectedFactory.toUpperCase().trim()
    //         return lineFactory === selected
    //     })

    //     if (filteredLines.length === 0) return

    //     try {
    //         const lineCodes = filteredLines.map(line => line.line).filter(code => code)
    //         if (lineCodes.length === 0) return

    //         const statusResponse = await api.get(`/api/asset/lines/status?factory=${selectedFactory}&lines=${lineCodes.join(',')}`)

    //         const updatedLines = filteredLines.map(line => {
    //             const statusInfo = statusResponse[line.line]
    //             if (statusInfo) {
    //                 return {
    //                     ...line,
    //                     // Actualizar solo el throughput, mantener el resto
    //                     //throughput: statusInfo.throughput || 0,
    //                     //last_update: statusInfo.last_seen || line.last_update
    //                 }
    //             }
    //             return line
    //         })

    //         setLines(updatedLines)
    //         setLastThroughputUpdate(new Date())
    //         console.log('Throughput updated at', new Date().toLocaleTimeString())
    //     } catch (err) {
    //         console.error('Error loading lines throughput:', err)
    //     }
    // }

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'running':
            case 'operando':
                return 'success'
            case 'stopped':
            case 'detenida':
                return 'danger'
            case 'maintenance':
            case 'mantenimiento':
                return 'warning'
            case 'setup':
            case 'preparación':
                return 'info'
            default:
                return 'secondary'
        }
    }

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'running':
            case 'operando':
                return <Activity className="text-green-500" size={20} />
            case 'stopped':
            case 'detenida':
                return <Zap className="text-red-500" size={20} />
            case 'maintenance':
            case 'mantenimiento':
                return <Clock className="text-yellow-500" size={20} />
            default:
                return <Activity className="text-gray-500" size={20} />
        }
    }

    const getStatusText = (status) => {
        switch (status?.toLowerCase()) {
            case 'running': return 'Operando'
            case 'stopped': return 'Detenida'
            case 'maintenance': return 'Mantenimiento'
            case 'setup': return 'Preparación'
            case 'unknown': return 'Sin datos'
            default: return status || 'Desconocido'
        }
    }

    const getFactoryFlag = (factoryCode) => {
        const flags = {
            'CXB': '🇧🇷', 'CXC': '🇨🇱', 'CXD': '🇩🇪', 'CXE': '🇪🇸',
            'CXF': '🇫🇷', 'CXM': '🇲🇽', 'EXT': '🇷🇺', 'FSP': '🇫🇷',
            'FPC': '🇫🇷', 'FPL': '🇫🇷', 'MNT': '🇮🇹', 'RTP': '🇪🇸', 'ITC': '🇮🇹'
        }
        return flags[factoryCode] || '🏭'
    }

    if (loading && lines.length === 0) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="animate-spin text-blue-500" size={48} />
            </div>
        )
    }

    if (error && lines.length === 0) {
        return (
            <div className="flex items-center justify-center h-96">
                <div className="text-center">
                    <p className="text-red-500 text-lg mb-4">{error}</p>
                    <button
                        onClick={loadLines}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white flex items-center gap-2 mx-auto"
                    >
                        <RefreshCw size={16} />
                        Reintentar
                    </button>
                </div>
            </div>
        )
    }

    // Agrupar líneas por fábrica
    const linesByFactory = lines.reduce((acc, line) => {
        if (!acc[line.factory]) {
            acc[line.factory] = []
        }
        acc[line.factory].push(line)
        return acc
    }, {})

    const factories = Object.keys(linesByFactory).sort()

    return (
        <div className="space-y-6 p-4 sm:p-6 w-full min-w-0">
            {/* Header */}
            <div className="flex items-center justify-between ">
                <div>
                    <h1 className="text-3xl pl-4 font-bold text-white flex items-center gap-3">
                        <Factory size={32} />
                        {selectedFactory ? (
                            <>
                                {getFactoryFlag(selectedFactory)}
                                Líneas de {selectedFactory}
                            </>
                        ) : (
                            'Líneas de Producción'
                        )}
                    </h1>
                    <p className="text-gray-400 mt-2">
                        Estado en tiempo real de {lines.length} {lines.length === 1 ? 'línea' : 'líneas'} de producción
                    </p>
                </div>

                <div className="flex items-center gap-4">
                    {lastUpdate && (
                        <span className="text-sm text-gray-500">
                            Actualizado: {lastUpdate.toLocaleTimeString()}
                        </span>
                    )}
                    <button
                        onClick={() => { loadLines(); refetchWorkOrders(); }}
                        disabled={loading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded text-white flex items-center gap-2"
                    >
                        <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                        Actualizar
                    </button>
                </div>
            </div>

            {/* Date Filters Card */}
            <Card className="p-4 bg-gray-900/40 border border-gray-800 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-300">Filtrar por Rango de Fechas:</span>
                </div>
                <div className="flex flex-wrap items-end gap-4">
                    <div className="flex flex-col">
                        <label className="text-[10px] text-gray-500 uppercase font-bold mb-1">Fecha Inicio</label>
                        <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            className="bg-gray-800 text-white border border-gray-700 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500 font-medium"
                        />
                    </div>
                    <div className="flex flex-col">
                        <label className="text-[10px] text-gray-500 uppercase font-bold mb-1">Fecha Fin</label>
                        <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            className="bg-gray-800 text-white border border-gray-700 rounded px-3 py-1.5 text-sm focus:outline-none focus:border-blue-500 font-medium"
                        />
                    </div>
                    {(startDate || endDate) && (
                        <button
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                            }}
                            className="px-3 py-1.5 text-xs bg-red-950/40 text-red-400 hover:text-red-300 border border-red-900/50 rounded font-semibold transition-colors h-[38px] flex items-center justify-center"
                        >
                            Limpiar Filtros
                        </button>
                    )}
                </div>
            </Card>

            {/* Content Below Date Filters with Loading Overlay */}
            <div className="relative min-h-[400px]">
                {(isLoadingWorkOrders || isFetchingWorkOrders) && (
                    <div className="absolute inset-0 bg-gray-950/60 backdrop-blur-[2px] z-30 flex flex-col items-center justify-start pt-28 rounded-xl transition-all">
                        <div className="sticky top-28 flex items-center gap-3 px-5 py-3 bg-gray-900/95 border border-gray-700/60 rounded-xl shadow-2xl">
                            <Loader2 className="animate-spin text-blue-500" size={24} />
                            <span className="text-sm font-medium text-gray-200">Cargando datos...</span>
                        </div>
                    </div>
                )}

                <div className={`space-y-6 ${(isLoadingWorkOrders || isFetchingWorkOrders) ? 'pointer-events-none opacity-50 transition-opacity' : ''}`}>
                    {/* Estadísticas Globales */}
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <Card>
                            <div className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-400">Total WO</p>
                                        <p className="text-2xl font-bold text-white">{allWorkOrders.length}</p>
                                    </div>
                                    <Factory className="text-blue-500" size={32} />
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <div className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-400">Reactive</p>
                                        <p className="text-2xl font-bold text-red-500">
                                            {allWorkOrders.filter(wo => wo.work_order_type === 'corrective').length}
                                        </p>
                                    </div>
                                    <Zap className="text-red-500" size={32} />
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <div className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-400">Predictive</p>
                                        <p className="text-2xl font-bold text-blue-500">
                                            {allWorkOrders.filter(wo => wo.work_order_type === 'predictive').length}
                                        </p>
                                    </div>
                                    <TrendingUp className="text-blue-500" size={32} />
                                </div>
                            </div>
                        </Card>

                        <Card>
                            <div className="p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm text-gray-400">Preventive</p>
                                        <p className="text-2xl font-bold text-lime-500">
                                            {allWorkOrders.filter(wo => wo.work_order_type === 'preventive').length}
                                        </p>
                                    </div>
                                    <Clock className="text-lime-500" size={32} />
                                </div>
                            </div>
                        </Card>
                    </div>

                    {/* Líneas - Vista por Fábrica Específica */}
                    {selectedFactory ? (
                        <div className="space-y-6">
                            <div className="flex gap-4">
                                <Card className="flex flex-col p-5 bg-gray-900/40 border border-gray-800 gap-2 w-1/2">
                                    <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Prioridades En Ordenes de Trabajo</h2>
                                    <div className="relative flex items-center justify-center">
                                        <svg width={`${graphdimensions.width}`} height={`${graphdimensions.height}`} className="-rotate-90">
                                            {/* Background Circle */}
                                            <circle
                                                cx={`${graphdimensions.width / 2}`}
                                                cy={`${graphdimensions.height / 2}`}
                                                r={`${graphdimensions.radius}`}
                                                className="stroke-gray-800 border "
                                                strokeWidth="6"
                                                fill="transparent"
                                            />
                                            {workOrdersByPriorities !== null ? (
                                                <>
                                                    {/* Low Priority */}
                                                    {workOrdersByPriorities.lowPct > 0 && (
                                                        <circle
                                                            cx={`${graphdimensions.width / 2}`}
                                                            cy={`${graphdimensions.height / 2}`}
                                                            r={`${graphdimensions.radius}`}
                                                            className="stroke-blue-400 border border-green-500"
                                                            strokeWidth="45"
                                                            fill="transparent"
                                                            strokeDasharray={`${(workOrdersByPriorities.lowPct / 100) * graphdimensions.perifery} ${graphdimensions.perifery}`}
                                                            strokeDashoffset=""
                                                        />
                                                    )}
                                                    {/* Medium Priority */}
                                                    {workOrdersByPriorities.mediumPct > 0 && (
                                                        <circle
                                                            cx={`${graphdimensions.width / 2}`}
                                                            cy={`${graphdimensions.height / 2}`}
                                                            r={`${graphdimensions.radius}`}
                                                            className="stroke-amber-400"
                                                            strokeWidth="45"
                                                            fill="transparent"
                                                            strokeDasharray={`${(workOrdersByPriorities.mediumPct / 100) * graphdimensions.perifery} ${graphdimensions.perifery}`}
                                                            strokeDashoffset={`-${(workOrdersByPriorities.lowPct / 100) * graphdimensions.perifery}`}
                                                        />
                                                    )}
                                                    {/* High Priority */}
                                                    {workOrdersByPriorities.highPct > 0 && (
                                                        <circle
                                                            cx={`${graphdimensions.width / 2}`}
                                                            cy={`${graphdimensions.height / 2}`}
                                                            r={`${graphdimensions.radius}`}
                                                            className="stroke-red-400 hover:border-red-500 transition-all cursor-pointer"
                                                            strokeWidth="45"
                                                            fill="transparent"
                                                            strokeDasharray={`${(workOrdersByPriorities.highPct / 100) * graphdimensions.perifery} ${graphdimensions.perifery}`}
                                                            strokeDashoffset={`-${((workOrdersByPriorities.lowPct + workOrdersByPriorities.mediumPct) / 100) * graphdimensions.perifery}`}
                                                        />
                                                    )}
                                                    {/* Critical Priority */}
                                                    {workOrdersByPriorities.criticalPct > 0 && (
                                                        <circle
                                                            cx={`${graphdimensions.width / 2}`}
                                                            cy={`${graphdimensions.height / 2}`}
                                                            r={`${graphdimensions.radius}`}
                                                            className="stroke-red-500"
                                                            strokeWidth="45"
                                                            fill="transparent"
                                                            strokeDasharray={`${(workOrdersByPriorities.criticalPct / 100) * graphdimensions.perifery} ${graphdimensions.perifery}`}
                                                            strokeDashoffset={`-${((workOrdersByPriorities.lowPct + workOrdersByPriorities.mediumPct + workOrdersByPriorities.highPct) / 100) * graphdimensions.perifery}`}
                                                        />
                                                    )}

                                                    {/* Percentage labels on top of strokes */}
                                                    {[
                                                        { pct: Number(workOrdersByPriorities.lowPct), start: 0 },
                                                        { pct: Number(workOrdersByPriorities.mediumPct), start: Number(workOrdersByPriorities.lowPct) },
                                                        { pct: Number(workOrdersByPriorities.highPct), start: Number(workOrdersByPriorities.lowPct) + Number(workOrdersByPriorities.mediumPct) },
                                                        { pct: Number(workOrdersByPriorities.criticalPct), start: Number(workOrdersByPriorities.lowPct) + Number(workOrdersByPriorities.mediumPct) + Number(workOrdersByPriorities.highPct) }
                                                    ].map((segment, index) => {
                                                        if (segment.pct <= 0) return null; // Only show text if segment is large enough
                                                        const midPct = segment.start + segment.pct / 2;
                                                        const { x, y } = getCoordinatesForPercent(midPct);
                                                        return (
                                                            <text
                                                                key={index}
                                                                x={x}
                                                                y={y}
                                                                fill="white"
                                                                fontSize="16"
                                                                fontWeight="bold"
                                                                textAnchor="middle"
                                                                dominantBaseline="central"
                                                                transform={`rotate(90, ${x}, ${y})`}
                                                            >
                                                                {segment.pct.toFixed(1)}%
                                                            </text>
                                                        );
                                                    })}
                                                </>
                                            ) : null}
                                        </svg>
                                        {/* Text inside the donut */}
                                        <div className="absolute flex flex-col items-center justify-center">
                                            <span className="text-3xl font-bold text-white leading-none">
                                                {((workOrdersByPriorities?.low || 0) + (workOrdersByPriorities?.medium || 0) + (workOrdersByPriorities?.high || 0) + (workOrdersByPriorities?.critical || 0)) || 0}
                                            </span>
                                            <span className="text-[10px] text-gray-400 font-semibold tracking-wider uppercase leading-none mt-1 mb-1">
                                                OT Totales
                                            </span>
                                            {workOrdersByPriorities !== null ? (
                                                <div className="mt-2 pt-2 border-t border-gray-700/50 w-36 grid grid-cols-2 gap-x-2 gap-y-1.5 text-[10px] text-left">
                                                    <div className="flex items-center gap-1 w-full justify-start pl-1">
                                                        <span className="w-2 h-2 rounded bg-red-500 shrink-0"></span>
                                                        <span className="text-gray-400">Crit: <span className="font-semibold text-white">{workOrdersByPriorities.critical || 0}</span></span>
                                                    </div>
                                                    <div className="flex items-center gap-1 w-full justify-start pl-1">
                                                        <span className="w-2 h-2 rounded bg-blue-400 shrink-0"></span>

                                                        <span className="text-gray-400">Low: <span className="font-semibold text-white">{workOrdersByPriorities.low || 0}</span></span>
                                                    </div>
                                                    <div className="flex items-center gap-1 w-full justify-start pl-1">
                                                        <span className="w-2 h-2 rounded bg-red-400 shrink-0"></span>
                                                        <span className="text-gray-400">High: <span className="font-semibold text-white">{workOrdersByPriorities.high || 0}</span></span>
                                                    </div>
                                                    <div className="flex items-center gap-1 w-full justify-start pl-1">
                                                        <span className="w-2 h-2 rounded bg-amber-400 shrink-0"></span>

                                                        <span className="text-gray-400">Med: <span className="font-semibold text-white">{workOrdersByPriorities.medium || 0}</span></span>
                                                    </div>
                                                </div>
                                            ) : (
                                                <p className="text-[10px] text-gray-500 italic mt-2">Sin órdenes de trabajo</p>
                                            )}
                                        </div>
                                    </div>

                                </Card>
                                <Card className="flex flex-col p-5 bg-gray-900/40 border border-gray-800 gap-2 w-1/2 justify-between">
                                    <div>
                                        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-2">Estado de Órdenes de Trabajo</h2>
                                        <div className="relative flex items-center justify-center">
                                            <svg width={`${graphdimensions.width}`} height={`${graphdimensions.height}`} className="-rotate-90">
                                                {/* Background Circle */}
                                                <circle
                                                    cx={`${graphdimensions.width / 2}`}
                                                    cy={`${graphdimensions.height / 2}`}
                                                    r={`${graphdimensions.radius}`}
                                                    className="stroke-gray-800 border "
                                                    strokeWidth="6"
                                                    fill="transparent"
                                                />
                                                {workOrdersByStatus !== null && workOrdersByStatus.total > 0 ? (
                                                    <>
                                                        {/* Scheduled WOs */}
                                                        {workOrdersByStatus.scheduledPct > 0 && (
                                                            <circle
                                                                cx={`${graphdimensions.width / 2}`}
                                                                cy={`${graphdimensions.height / 2}`}
                                                                r={`${graphdimensions.radius}`}
                                                                className="stroke-blue-400"
                                                                strokeWidth="45"
                                                                fill="transparent"
                                                                strokeDasharray={`${(workOrdersByStatus.scheduledPct / 100) * graphdimensions.perifery} ${graphdimensions.perifery}`}
                                                                strokeDashoffset=""
                                                            />
                                                        )}
                                                        {/* In Progress WOs */}
                                                        {workOrdersByStatus.inProgressPct > 0 && (
                                                            <circle
                                                                cx={`${graphdimensions.width / 2}`}
                                                                cy={`${graphdimensions.height / 2}`}
                                                                r={`${graphdimensions.radius}`}
                                                                className="stroke-amber-400"
                                                                strokeWidth="45"
                                                                fill="transparent"
                                                                strokeDasharray={`${(workOrdersByStatus.inProgressPct / 100) * graphdimensions.perifery} ${graphdimensions.perifery}`}
                                                                strokeDashoffset={`-${(workOrdersByStatus.scheduledPct / 100) * graphdimensions.perifery}`}
                                                            />
                                                        )}
                                                        {/* Delayed WOs */}
                                                        {workOrdersByStatus.delayedPct > 0 && (
                                                            <circle
                                                                cx={`${graphdimensions.width / 2}`}
                                                                cy={`${graphdimensions.height / 2}`}
                                                                r={`${graphdimensions.radius}`}
                                                                className="stroke-red-700"
                                                                strokeWidth="45"
                                                                fill="transparent"
                                                                strokeDasharray={`${(workOrdersByStatus.delayedPct / 100) * graphdimensions.perifery} ${graphdimensions.perifery}`}
                                                                strokeDashoffset={`-${((workOrdersByStatus.scheduledPct + workOrdersByStatus.inProgressPct) / 100) * graphdimensions.perifery}`}
                                                            />
                                                        )}
                                                        {/* Completed WOs */}
                                                        {workOrdersByStatus.completedPct > 0 && (
                                                            <circle
                                                                cx={`${graphdimensions.width / 2}`}
                                                                cy={`${graphdimensions.height / 2}`}
                                                                r={`${graphdimensions.radius}`}
                                                                className="stroke-emerald-500"
                                                                strokeWidth="45"
                                                                fill="transparent"
                                                                strokeDasharray={`${(workOrdersByStatus.completedPct / 100) * graphdimensions.perifery} ${graphdimensions.perifery}`}
                                                                strokeDashoffset={`-${((workOrdersByStatus.scheduledPct + workOrdersByStatus.inProgressPct + workOrdersByStatus.delayedPct) / 100) * graphdimensions.perifery}`}
                                                            />
                                                        )}

                                                        {/* Percentage labels on top of strokes */}
                                                        {[
                                                            { pct: Number(workOrdersByStatus.scheduledPct), start: 0 },
                                                            { pct: Number(workOrdersByStatus.inProgressPct), start: Number(workOrdersByStatus.scheduledPct) },
                                                            { pct: Number(workOrdersByStatus.delayedPct), start: Number(workOrdersByStatus.scheduledPct) + Number(workOrdersByStatus.inProgressPct) },
                                                            { pct: Number(workOrdersByStatus.completedPct), start: Number(workOrdersByStatus.scheduledPct) + Number(workOrdersByStatus.inProgressPct) + Number(workOrdersByStatus.delayedPct) }
                                                        ].map((segment, index) => {
                                                            if (segment.pct <= 0) return null; // Only show text if segment is large enough
                                                            const midPct = segment.start + segment.pct / 2;
                                                            const { x, y } = getCoordinatesForPercent(midPct);
                                                            return (
                                                                <text
                                                                    key={index}
                                                                    x={x}
                                                                    y={y}
                                                                    fill="white"
                                                                    fontSize="16"
                                                                    fontWeight="bold"
                                                                    textAnchor="middle"
                                                                    dominantBaseline="central"
                                                                    transform={`rotate(90, ${x}, ${y})`}
                                                                >
                                                                    {segment.pct.toFixed(1)}%
                                                                </text>
                                                            );
                                                        })}
                                                    </>
                                                ) : null}
                                            </svg>
                                            {/* Text inside the donut */}
                                            <div className="absolute flex flex-col items-center justify-center">
                                                <span className="text-3xl font-bold text-white leading-none">
                                                    {workOrdersByStatus.total}
                                                </span>
                                                <span className="text-[10px] text-gray-400 font-semibold tracking-wider uppercase leading-none mt-1 mb-1">
                                                    OT Totales
                                                </span>
                                                {workOrdersByStatus.total > 0 ? (
                                                    <div className="mt-2 pt-2 border-t border-gray-700/50 w-36 grid grid-cols-2 gap-x-2 gap-y-1.5 text-[10px] text-left">
                                                        <div className="flex items-center gap-1 w-full justify-start pl-1">
                                                            <span className="w-2 h-2 rounded bg-emerald-500 shrink-0"></span>
                                                            <span className="text-gray-400">Comp: <span className="font-semibold text-white">{workOrdersByStatus.completed}</span></span>
                                                        </div>
                                                        <div className="flex items-center gap-1 w-full justify-start pl-1">
                                                            <span className="w-2 h-2 rounded bg-blue-400 shrink-0"></span>
                                                            <span className="text-gray-400">Sched: <span className="font-semibold text-white">{workOrdersByStatus.scheduled}</span></span>
                                                        </div>
                                                        <div className="flex items-center gap-1 w-full justify-start pl-1">
                                                            <span className="w-2 h-2 rounded bg-red-700 shrink-0"></span>
                                                            <span className="text-gray-400">Delay: <span className="font-semibold text-white">{workOrdersByStatus.delayed}</span></span>
                                                        </div>
                                                        <div className="flex items-center gap-1 w-full justify-start pl-1">
                                                            <span className="w-2 h-2 rounded bg-amber-400 shrink-0"></span>
                                                            <span className="text-gray-400">In Prog: <span className="font-semibold text-white">{workOrdersByStatus.inProgress}</span></span>
                                                        </div>


                                                    </div>
                                                ) : (
                                                    <p className="text-[10px] text-gray-500 italic mt-2">Sin órdenes de trabajo</p>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Barra de Progreso de Calidad en OTs Completadas */}
                                    <div className="mt-3 pt-3 border-t border-gray-800/80 w-full px-2">
                                        <div className="flex items-center justify-between text-xs mb-1.5">
                                            <span className="text-gray-300 font-semibold text-xs tracking-wide">
                                                Calidad en Completadas ({workOrdersByStatus.completed} OTs)
                                            </span>
                                            {workOrdersByStatus.completed > 0 ? (
                                                <span className="font-bold text-emerald-400 font-mono text-xs">
                                                    {workOrdersByStatus.successRate.toFixed(1)}% Éxito
                                                </span>
                                            ) : (
                                                <span className="text-gray-500 text-xs italic">Sin OTs completadas</span>
                                            )}
                                        </div>

                                        {/* Dual colored Progress Bar */}
                                        <div className="w-full h-2.5 bg-gray-800/80 rounded-full overflow-hidden flex">
                                            {workOrdersByStatus.completed > 0 ? (
                                                <>
                                                    <div
                                                        style={{ width: `${workOrdersByStatus.passedPct}%` }}
                                                        className="bg-emerald-500 h-full transition-all duration-500"
                                                        title={`Éxito: ${workOrdersByStatus.passed} (${workOrdersByStatus.passedPct.toFixed(1)}%)`}
                                                    />
                                                    <div
                                                        style={{ width: `${workOrdersByStatus.failedPct}%` }}
                                                        className="bg-red-500 h-full transition-all duration-500"
                                                        title={`Fallo: ${workOrdersByStatus.failed} (${workOrdersByStatus.failedPct.toFixed(1)}%)`}
                                                    />
                                                </>
                                            ) : (
                                                <div className="w-full h-full bg-gray-800" />
                                            )}
                                        </div>

                                        <div className="flex items-center justify-between text-[11px] text-gray-400 mt-1.5 font-medium">
                                            <div className="flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block"></span>
                                                <span>Éxito: <strong className="text-white font-mono">{workOrdersByStatus.passed}</strong></span>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-red-500 inline-block"></span>
                                                <span>Fallo / Incidencias: <strong className="text-white font-mono">{workOrdersByStatus.failed}</strong></span>
                                            </div>
                                        </div>
                                    </div>

                                </Card>
                            </div>
                            <div className="flex gap-4">
                                <div className="w-1/2">
                                    {/* Bar Chart comparing total work orders per line */}
                                    <KPIBar filteredWorkOrders={filteredWorkOrders} chartJsLoaded={chartJsLoaded} />
                                </div>
                                <div className="w-1/2">
                                    {/* Line Chart showing work orders per month in the last 6 months */}
                                    <KpiLine allWorkOrders={allWorkOrders} chartJsLoaded={chartJsLoaded} selectedLine={selectedLine} />
                                </div>
                            </div>

                            {/* 2 Columns: Lines on Left, Work Orders Detail Box on Right */}
                            <div className="flex flex-col lg:flex-row gap-6 items-start">
                                {/* Column 1: Line Cards */}
                                <div className="w-full lg:w-1/2 flex flex-col gap-4">
                                    <div className="flex items-center justify-between">
                                        <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">
                                            Líneas de Producción ({filteredWorkOrders.length})
                                        </h2>
                                        <span className="text-xs text-gray-500">
                                            Selecciona una línea para ver sus OTs
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[850px] overflow-y-auto pr-1">
                                        {filteredWorkOrders.map(line => {
                                            const lineExecutedWOs = [
                                                ...(line.lineWorkOrders?.correctiveWorkOrders || []),
                                                ...(line.lineWorkOrders?.predictiveWorkOrders || []),
                                                ...(line.lineWorkOrders?.preventiveWorkOrders || [])
                                            ].filter(wo => wo.status === 'completed' || wo.status === 'in_progress' || !!wo.start_date);

                                            const isTreeExpanded = expandedLineTrees.has(line.id);

                                            return (
                                                <Card key={line.id}
                                                    className={`hover:border-blue-500 hover:cursor-pointer transition-all ${selectedLine?.id === line?.id ? 'border-blue-500 bg-blue-950/20 ring-1 ring-blue-500/50' : ''
                                                        }`}
                                                    onClick={() => {
                                                        if (selectedLine?.id !== line.id) {
                                                            setSelectedLine(line);
                                                            setSelectedAssetCode(null);
                                                        } else {
                                                            setSelectedLine(null);
                                                            setSelectedAssetCode(null);
                                                        }
                                                    }}>
                                                    <div className="p-4">
                                                        {/* Header */}
                                                        <div className="flex items-start justify-between mb-3">
                                                            <div className="flex items-center gap-2">
                                                                {getStatusIcon(line.status)}
                                                                <div>
                                                                    <h3 className="text-lg font-semibold text-white">
                                                                        {line.line}
                                                                    </h3>
                                                                    <p className="text-xs text-gray-500">{line.location}</p>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-2">
                                                                <Badge variant={getStatusColor(line.status)}>
                                                                    {getStatusText(line.status)}
                                                                </Badge>
                                                            </div>
                                                        </div>

                                                        {/* Métricas */}
                                                        {line.influx_available ? (
                                                            <div className="space-y-2">
                                                                <div className="flex items-center justify-between text-sm">
                                                                    <span className="text-gray-400">OEE</span>
                                                                    <span className={`font-semibold ${line.oee >= 85 ? 'text-green-500' :
                                                                        line.oee >= 70 ? 'text-yellow-500' :
                                                                            'text-red-500'
                                                                        }`}>
                                                                        {line.oee?.toFixed(1)}%
                                                                    </span>
                                                                </div>

                                                                <div className="flex items-center justify-between text-sm">
                                                                    <span className="text-gray-400">Throughput</span>
                                                                    <span className="font-mono text-white">
                                                                        {typeof line.throughput === 'number' && !isNaN(line.throughput)
                                                                            ? line.throughput.toFixed(1)
                                                                            : (typeof line.speed === 'number' && !isNaN(line.speed)
                                                                                ? line.speed.toFixed(1)
                                                                                : '0.0')} kg/h
                                                                    </span>
                                                                </div>

                                                                {line.production > 0 && (
                                                                    <div className="flex items-center justify-between text-sm">
                                                                        <span className="text-gray-400">Producción</span>
                                                                        <span className="font-mono text-white">
                                                                            {line.production?.toFixed(0)} m
                                                                        </span>
                                                                    </div>
                                                                )}

                                                                {line.last_update && (
                                                                    <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-700">
                                                                        {new Date(line.last_update).toLocaleTimeString()}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div className="flex flex-col justify-center py-2">
                                                                <div className='flex flex-row gap-4 items-center'>
                                                                    <div className="relative flex self-start items-center justify-center">
                                                                        {/* Donut Chart SVG */}
                                                                        <svg width="72" height="72" className="-rotate-90">
                                                                            {/* Background Circle */}
                                                                            <circle
                                                                                cx="36"
                                                                                cy="36"
                                                                                r="28"
                                                                                className="stroke-gray-800"
                                                                                strokeWidth="6"
                                                                                fill="transparent"
                                                                            />
                                                                            {line?.workOrderStats?.total > 0 ? (
                                                                                <>
                                                                                    {/* Corrective (Red) */}
                                                                                    {line.workOrderStats.correctivePct > 0 && (
                                                                                        <circle
                                                                                            cx="36"
                                                                                            cy="36"
                                                                                            r="28"
                                                                                            className="stroke-red-500"
                                                                                            strokeWidth="6"
                                                                                            fill="transparent"
                                                                                            strokeDasharray={`${(line.workOrderStats.correctivePct / 100) * 175.9} 175.9`}
                                                                                            strokeDashoffset=""
                                                                                        />
                                                                                    )}
                                                                                    {/* Predictive (Blue) */}
                                                                                    {line.workOrderStats.predictivePct > 0 && (
                                                                                        <circle
                                                                                            cx="36"
                                                                                            cy="36"
                                                                                            r="28"
                                                                                            className="stroke-blue-500"
                                                                                            strokeWidth="6"
                                                                                            fill="transparent"
                                                                                            strokeDasharray={`${(line.workOrderStats.predictivePct / 100) * 175.9} 175.9`}
                                                                                            strokeDashoffset={`-${(line.workOrderStats.correctivePct / 100) * 175.9}`}
                                                                                        />
                                                                                    )}
                                                                                    {/* Preventive (Lime) */}
                                                                                    {line.workOrderStats.preventivePct > 0 && (
                                                                                        <circle
                                                                                            cx="36"
                                                                                            cy="36"
                                                                                            r="28"
                                                                                            className="stroke-lime-500"
                                                                                            strokeWidth="6"
                                                                                            fill="transparent"
                                                                                            strokeDasharray={`${(line.workOrderStats.preventivePct / 100) * 175.9} 175.9`}
                                                                                            strokeDashoffset={`-${((line.workOrderStats.correctivePct + line.workOrderStats.predictivePct) / 100) * 175.9}`}
                                                                                        />
                                                                                    )}
                                                                                </>
                                                                            ) : null}
                                                                        </svg>

                                                                        {/* Text inside the donut */}
                                                                        <div className="absolute flex flex-col items-center justify-center">
                                                                            <span className="text-lg font-bold text-white leading-none">
                                                                                {line?.workOrderStats?.total || 0}
                                                                            </span>
                                                                            <span className="text-[9px] text-gray-400 font-medium tracking-wider uppercase leading-none mt-0.5">
                                                                                OT
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    {/* Stats Block */}
                                                                    {!line.availabilityStats ? (
                                                                        <div className="flex flex-col gap-0.5 text-right font-mono text-[10px] text-gray-500">
                                                                            <p>MTBF: N/A</p>
                                                                            <p>MTTR: N/A</p>
                                                                            <p>Disp: N/A</p>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex flex-col gap-0.5 text-left font-mono text-[10px] text-white">
                                                                            <p>MTBF: <span className="text-blue-400 font-bold">{line.availabilityStats.MTBF[0]} {line.availabilityStats.MTBF[1]}</span></p>
                                                                            <p>MTTR: <span className="text-yellow-400 font-bold">{line.availabilityStats.MTTR[0]} {line.availabilityStats.MTTR[1]}</span></p>
                                                                            <p>Disp: <span className="text-green-400 font-bold">{line.availabilityStats.availability[0]}%</span></p>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Label / Legend */}
                                                                <div className="mt-3 text-center">
                                                                    {line?.workOrderStats?.total > 0 ? (
                                                                        <div className="flex items-center gap-3 text-[10px] text-gray-300">
                                                                            <span className="flex items-center gap-1">
                                                                                <span className="w-2.5 h-2.5 rounded bg-red-500 inline-block"></span>
                                                                                <span>{line.lineWorkOrders.correctiveWorkOrders.length} Corr.</span>
                                                                            </span>
                                                                            <span className="flex items-center gap-1">
                                                                                <span className="w-2.5 h-2.5 rounded bg-blue-500 inline-block"></span>
                                                                                <span>{line.lineWorkOrders.predictiveWorkOrders.length} Pred.</span>
                                                                            </span>
                                                                            <span className="flex items-center gap-1">
                                                                                <span className="w-2.5 h-2.5 rounded bg-lime-500 inline-block"></span>
                                                                                <span>{line.lineWorkOrders.preventiveWorkOrders.length} Prev.</span>
                                                                            </span>
                                                                        </div>
                                                                    ) : (
                                                                        <p className="text-xs text-gray-500 italic">Sin órdenes de trabajo</p>
                                                                    )}
                                                                </div>
                                                                {line.workOrderStats.total > 0 && (
                                                                    <div className="mt-2">
                                                                        <p className="text-white text-sm font-mono">Tasa de Éxito: {line.successRate.toFixed(2)}%</p>
                                                                        <p className="text-white text-sm font-mono">OTs / Mes: {line.monthlyRate.toFixed(2)}</p>
                                                                        <p className="text-white text-sm font-mono">OTs / Semana: {line.weeklyRate.toFixed(2)}</p>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}

                                                        {/* Deployable Dropdown: Intervened Children Assets */}
                                                        <div className="mt-3 pt-3 border-t border-gray-800/80">
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleLineTree(line.id);
                                                                }}
                                                                className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${isTreeExpanded
                                                                    ? 'bg-blue-950/60 text-blue-300 border border-blue-800/50'
                                                                    : 'bg-gray-800/60 text-gray-400 hover:text-gray-200 hover:bg-gray-800 border border-transparent'
                                                                    }`}
                                                            >
                                                                <div className="flex items-center gap-1.5">
                                                                    <GitBranch size={13} className={isTreeExpanded ? 'text-blue-400' : 'text-gray-400'} />
                                                                    <span>Activos Intervenidos</span>
                                                                    {lineExecutedWOs.length > 0 && (
                                                                        <span className="text-[10px] px-1.5 py-0.5 bg-blue-900/50 text-blue-300 rounded font-mono">
                                                                            {lineExecutedWOs.length}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                {isTreeExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                                            </button>

                                                            {isTreeExpanded && (
                                                                <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                                                                    <KpiAssetTree
                                                                        lineCode={line.line}
                                                                        executedWorkOrders={lineExecutedWOs}
                                                                        selectedAssetCode={selectedLine?.id === line.id ? selectedAssetCode : null}
                                                                        onSelectAssetCode={(code, e) => handleSelectAsset(line, code, e)}
                                                                    />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Card>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Column 2: Selected Line Work Orders Detail Box */}
                                <div className="w-full lg:w-1/2 sticky top-6">
                                    <KpiLineDetails
                                        selectedLine={selectedLine}
                                        filteredWorkOrders={filteredWorkOrders}
                                        selectedAssetCode={selectedAssetCode}
                                        onClearAsset={() => setSelectedAssetCode(null)}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        /* Vista General - Agrupada por Fábrica */
                        <>
                            {factories.map(factory => (
                                <div key={factory}>
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                                            <span>{getFactoryFlag(factory)}</span>
                                            <span>{factory}</span>
                                            <span className="text-sm text-gray-500">
                                                ({linesByFactory[factory].length} {linesByFactory[factory].length === 1 ? 'línea' : 'líneas'})
                                            </span>
                                        </h2>

                                        {/* Solo usuarios con acceso a CX pueden ver todas las fábricas */}
                                        {permissions && checkFactoryAccess('CX') && (
                                            <button
                                                onClick={() => {
                                                    setSelectedFactory(factory)
                                                    loadLines()
                                                }}
                                                className="px-4 py-2 text-sm bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors flex items-center gap-2"
                                            >
                                                Ver todas las líneas de {factory}
                                                <ChevronRight size={16} />
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                        {linesByFactory[factory].map(line => (
                                            <Card key={line.id} className="hover:border-blue-500 transition-all">
                                                <div className="p-4">
                                                    {/* Header */}
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            {getStatusIcon(line.status)}
                                                            <div>
                                                                <h3 className="text-lg font-semibold text-white">
                                                                    {line.line}
                                                                </h3>
                                                                <p className="text-xs text-gray-500">{line.location}</p>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant={getStatusColor(line.status)}>
                                                                {getStatusText(line.status)}
                                                            </Badge>
                                                        </div>
                                                    </div>

                                                    {/* Métricas */}
                                                    {line.influx_available ? (
                                                        <div className="space-y-2">
                                                            <div className="flex items-center justify-between text-sm">
                                                                <span className="text-gray-400">OEE</span>
                                                                <span className={`font-semibold ${line.oee >= 85 ? 'text-green-500' :
                                                                    line.oee >= 70 ? 'text-yellow-500' :
                                                                        'text-red-500'
                                                                    }`}>
                                                                    {line.oee?.toFixed(1)}%
                                                                </span>
                                                            </div>

                                                            <div className="flex items-center justify-between text-sm">
                                                                <span className="text-gray-400">Throughput</span>
                                                                <span className="font-mono text-white">
                                                                    {typeof line.throughput === 'number' && !isNaN(line.throughput)
                                                                        ? line.throughput.toFixed(1)
                                                                        : (typeof line.speed === 'number' && !isNaN(line.speed)
                                                                            ? line.speed.toFixed(1)
                                                                            : '0.0')} kg/h
                                                                </span>
                                                            </div>

                                                            {line.production > 0 && (
                                                                <div className="flex items-center justify-between text-sm">
                                                                    <span className="text-gray-400">Producción</span>
                                                                    <span className="font-mono text-white">
                                                                        {line.production?.toFixed(0)} m
                                                                    </span>
                                                                </div>
                                                            )}

                                                            {line.last_update && (
                                                                <div className="text-xs text-gray-600 mt-2 pt-2 border-t border-gray-700">
                                                                    {new Date(line.last_update).toLocaleTimeString()}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="text-center text-gray-600 py-4">
                                                            <p className="text-xs">Sin conexión InfluxDB</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </Card>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </>
                    )}

                    {lines.length === 0 && !loading && (
                        <Card>
                            <div className="p-12 text-center text-gray-500">
                                <Factory size={48} className="mx-auto mb-4 opacity-50" />
                                <p>No se encontraron líneas de producción</p>
                            </div>
                        </Card>
                    )}
                </div>
            </div>

        </div>
    )
}

export default KPI

